// ============================================================
// @munawb/hooks — useIncidents
// مشترك بين Web و Mobile
// ============================================================

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  fetchIncidents, fetchIncidentById,
  createIncident, performIncidentAction,
  type FetchIncidentsOptions
} from '@munawb/supabase';
import type { CreateIncidentRequest, IncidentActionRequest } from '@munawb/types';

// ==============================
// Query Keys
// ==============================
export const incidentKeys = {
  all:    ['incidents']                            as const,
  list:   (f: FetchIncidentsOptions) => ['incidents', 'list', f] as const,
  detail: (id: string) => ['incidents', 'detail', id]            as const,
};

// ==============================
// Hooks
// ==============================

export function useIncidents(filters: FetchIncidentsOptions = {}) {
  return useQuery({
    queryKey:    incidentKeys.list(filters),
    queryFn:     () => fetchIncidents(filters),
    staleTime:   30_000,
    placeholderData: keepPreviousData,
  });
}

export function useIncident(id: string) {
  return useQuery({
    queryKey: incidentKeys.detail(id),
    queryFn:  () => fetchIncidentById(id),
    enabled:  !!id,
    staleTime: 15_000,
  });
}

export function useCreateIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateIncidentRequest & { reporter_name: string }) =>
      createIncident(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: incidentKeys.all });
    },
  });
}

export function useIncidentAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: IncidentActionRequest) => performIncidentAction(data),
    onSuccess: (_result, variables) => {
      // إبطال كاش الحدث المحدد + القائمة الكاملة
      qc.invalidateQueries({ queryKey: incidentKeys.detail(variables.incident_id) });
      qc.invalidateQueries({ queryKey: incidentKeys.all });
    },
  });
}

// ==============================
// SLA Calculator (مشترك مع Mobile)
// ==============================
export function useSlaStatus(
  created_at: string,
  sla_minutes: number,
  closed_at: string | null,
  status: string
) {
  const isClosed   = status === 'مغلق' || status === 'RCA Completed';
  const refTime    = isClosed && closed_at ? new Date(closed_at).getTime() : Date.now();
  const startTime  = new Date(created_at).getTime();
  const slaMsTotal = sla_minutes * 60 * 1000;
  const elapsed    = refTime - startTime;
  const remaining  = slaMsTotal - elapsed;
  const pct        = Math.min(100, Math.round((elapsed / slaMsTotal) * 100));
  const isBreached = elapsed > slaMsTotal;

  const formatRemaining = () => {
    if (isBreached) return 'تجاوز SLA';
    const mins = Math.floor(remaining / 60_000);
    if (mins < 60)  return `${mins} دقيقة`;
    const hrs = Math.floor(mins / 60);
    if (hrs  < 24)  return `${hrs} ساعة`;
    return `${Math.floor(hrs / 24)} يوم`;
  };

  return { pct, isBreached, isClosed, formatRemaining };
}
